import Vue from "vue";
import store from "@/store";
import {
  AnyObj,
  IFilterList,
  IFilterListSignature,
  IFilterOptions,
  IFilterSelected,
  IProcessFilterList
} from "./utils-types";
import { PROCESS_DATE_TYPE as dateType } from "@/utils/enumerate";
import { IFilesPage } from "@/store/types/file-type";

Vue.filter("CpfMaskFilter", (element: string | number) => {
  element = typeof element !== "string" ? element.toString() : element;
  element = element.padStart(11, "0");
  element = element.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  return element;
});

const getFiltersIds = ({
  bookmarks,
  user_document,
  document_type,
  origin,
  origin_capacity,
  current_capacity
}: IFilterList) => {
  const finalData: any = {};

  if (bookmarks?.length)
    finalData.bookmarks = JSON.stringify(bookmarks.map(({ id }) => id));

  if (user_document?.length)
    finalData.user = JSON.stringify(user_document.map(({ id }) => id));

  if (document_type?.length)
    finalData.document_type = JSON.stringify(document_type.map(({ id }) => id));

  if (origin?.length)
    finalData.origin = JSON.stringify(origin.map(({ id }) => id));

  if (origin_capacity?.length)
    finalData.origin_capacity = JSON.stringify(
      origin_capacity.map(({ id }) => id)
    );

  if (current_capacity?.length)
    finalData.capacity_filter = JSON.stringify(
      current_capacity.map(({ id }) => id)
    );

  return finalData;
};

const getFiltersText = ({
  can_return_doe,
  status_doe,
  responsible,
  ccs,
  status_signature,
  document_status_doe
}: IFilterList) => {
  const finalData: any = {};

  if (store.getters.hasSomePermission(["AUTHORIZE_DOE"]) && can_return_doe)
    finalData.can_return_doe = can_return_doe?.displayText;

  if (status_doe) finalData.status_doe = status_doe.displayText;

  if (responsible?.length)
    finalData.user_responsible_name = JSON.stringify(
      responsible.map(({ name }) => name)
    );

  if (ccs?.length)
    finalData.ccs = JSON.stringify(ccs.map(({ displayText }) => displayText));

  if (status_signature?.length)
    finalData.status_signature = JSON.stringify(
      status_signature.map(({ displayText }) => displayText)
    );

  if (document_status_doe?.length)
    finalData.document_status_doe = JSON.stringify(
      document_status_doe.map(({ displayText }) => displayText)
    );

  return finalData;
};

export const createFilterData = (
  filterList: IFilterList,
  processTab: string
) => {
  const {
    order_by_urgent,
    analysis,
    users = [],
    capacities = [],
    date,
    date_created,
    processfile_id,
    current_origin
  } = filterList || {};
  const hasAnalysis = analysis?.displayText;
  const finalData: AnyObj = {};

  Object.assign(finalData, getFiltersIds(filterList));
  Object.assign(finalData, getFiltersText(filterList));

  if (processfile_id?.fileId?.length)
    finalData.processfile_id = JSON.stringify(
      processfile_id.fileId.reduce((acc: string[], actual: string) => {
        if (!acc.includes(actual)) acc.push(actual);
        return acc;
      }, [])
    );

  if (typeof order_by_urgent?.displayText === "boolean")
    finalData.order_by_urgent = order_by_urgent.displayText;

  if (hasAnalysis?.length) {
    const processingOptions = {
      IN_AUTO_PROCESS: true,
      WITHOUT_AUTO_PROCESS: false
    };
    const analysisOptions = {
      IN_ANALYSIS: true,
      WITHOUT_RESPONSIBLE: false
    };
    type StatusTypes = keyof (
      | typeof analysisOptions
      | typeof processingOptions
    );

    finalData.analysis = analysisOptions[hasAnalysis as StatusTypes];
    finalData.automatic_processing =
      processingOptions[hasAnalysis as StatusTypes];

    const flagStatus: Record<
      string,
      "returned" | "migrated" | "archived" | "recently_unarchived"
    > = {
      RETURNED: "returned",
      MIGRATE: "migrated",
      ARCHIVED: "archived",
      UNARCHIVED: "recently_unarchived"
    };

    if (flagStatus[hasAnalysis]) finalData[flagStatus[hasAnalysis]] = true;
  }

  if (users?.length || capacities?.length)
    finalData.ccs = JSON.stringify({
      users: users?.map(({ id }) => id),
      capacities: capacities?.map(({ id }) => id)
    });

  if (current_origin?.length)
    finalData.current_part = JSON.stringify(
      current_origin?.map(({ id }) => id)
    );

  if (processTab) {
    finalData.date = JSON.stringify({
      date: date_created,
      [dateType[processTab as keyof typeof dateType]]: date
    });
  } else if (date?.start && date?.end) {
    finalData.date = JSON.stringify(date);
  }

  return finalData;
};

export const createFilterSignature = (filterList: IFilterListSignature) => {
  const situation = filterList?.status_signature?.displayText || null,
    capacities = filterList?.capacities?.map(filter => filter.id) || [],
    users = filterList?.users?.map(filter => filter.id) || [],
    document_type = filterList?.document_type?.map(filter => filter.id);

  let finalData = {},
    bookmarks: string[] = [],
    global_bookmarks: string[] = [];

  filterList?.bookmarks?.forEach(bookmark => {
    if (bookmark.is_global)
      global_bookmarks = [...global_bookmarks, bookmark.id];
    else bookmarks = [...bookmarks, bookmark.id];
  });

  if (document_type?.length) {
    const formatedDocumentType = JSON.stringify(document_type);
    finalData = { ...finalData, document_type: formatedDocumentType };
  }

  if (bookmarks?.length) {
    const formatedBookmark = JSON.stringify(bookmarks);
    finalData = { ...finalData, bookmarks: formatedBookmark };
  }

  if (global_bookmarks?.length) {
    const formatedGlobalBookmark = JSON.stringify(global_bookmarks);
    finalData = { ...finalData, global_bookmarks: formatedGlobalBookmark };
  }

  if (capacities?.length || users?.length) {
    const formatedCcs = JSON.stringify({ capacities, users });
    finalData = { ...finalData, ccs: formatedCcs };
  }

  if (filterList?.date?.start && filterList?.date?.end) {
    const formatedDate = JSON.stringify({
      start: filterList?.date?.start,
      end: filterList?.date?.end
    });
    finalData = { ...finalData, date: formatedDate };
  }

  if (situation !== null) {
    finalData = { ...finalData, situation };
  }

  return finalData;
};

export const checkSelectFunction = (
  currentFilterList: IFilterSelected[],
  optionsList: IFilterOptions[],
  actualIndex: number
) => {
  const indexTypes = currentFilterList.map(filter => filter.type);
  return optionsList.filter(filter => {
    const itemIndex = indexTypes.indexOf(filter.type);
    return filter.unique ? itemIndex < 0 || itemIndex === actualIndex : true;
  });
};

export const processfilterData = (filterList: IProcessFilterList) => {
  const origin = filterList?.origin?.map(filter => filter.data.id),
    subject = filterList?.subject?.map(filter => filter.data.completeName),
    origin_capacity = filterList?.origin_capacity?.map(
      filter => filter.data.id
    ),
    reference_number = filterList?.reference_number?.map(
      filter => filter.data.displayText
    );

  let finalData = {};

  if (filterList?.date?.data?.start && filterList?.date?.data?.end) {
    const date = {
      start: filterList.date.data.start,
      end: filterList.date.data.end
    };
    finalData = { ...finalData, date: { date } };
  }

  if (origin?.length) {
    const formatedOrigin = origin;
    finalData = { ...finalData, origin: formatedOrigin };
  }

  if (subject?.length) {
    finalData = { ...finalData, subject };
  }

  if (origin_capacity?.length) {
    const formatedOriginCapacities = origin_capacity;
    finalData = {
      ...finalData,
      origin_capacity: formatedOriginCapacities
    };
  }

  if (reference_number?.length) {
    finalData = { ...finalData, reference_number };
  }

  return finalData;
};

export const nextFileNumberPage = (filePages: IFilesPage[], number: string) => {
  const foundFile =
    filePages.find((file, currentIndex) => {
      const nextPage = filePages[currentIndex + 1]?.page ?? Infinity;
      const pageParam = Number(number);
      return nextPage > pageParam && pageParam >= file.page;
    }) ?? filePages[filePages.length - 1];
  return foundFile?.processfile_id;
};
